import type { BandKey, RagBand } from './ragProfiles.types';
import { BAND_KEY_ORDER } from './ragProfiles.types';

const PREVIEW_WIDTH = 440;
const PREVIEW_HEIGHT = 200;

// Mirrors the main KrChart band fills (wobo-components buildRagGapBandSeries)
export const BAND_OPACITY: Record<BandKey, number> = {
  above_plan: 0.16,
  on_plan: 0.18,
  medium_risk: 0.14,
  at_risk: 0.16,
};

type PreviewEdge = number | 'chartMin' | 'chartMax';

type PreviewBandRegion = {
  key: BandKey;
  upperEdge: PreviewEdge;
  lowerEdge: PreviewEdge;
};

export interface PreviewBandPolygon {
  key: BandKey;
  color: string;
  opacity: number;
  points: string;
}

const derivePreviewBandRegions = (bands: RagBand[]): PreviewBandRegion[] => {
  const enabled = bands.filter((band) => band.enabled);
  if (!enabled.length) {
    return [];
  }

  const regions: PreviewBandRegion[] = [];
  let previousThreshold = 0;

  for (let i = 0; i < enabled.length - 1; i += 1) {
    const band = enabled[i];
    const thresholdValue = band.threshold;
    if (thresholdValue !== null && thresholdValue !== undefined) {
      const threshold = Number(thresholdValue);
      if (!Number.isNaN(threshold)) {
        regions.push({
          key: band.key,
          upperEdge: i === 0 ? 'chartMax' : previousThreshold,
          lowerEdge: threshold,
        });
        previousThreshold = threshold;
      }
    }
  }

  const worst = enabled[enabled.length - 1];
  regions.push({
    key: worst.key,
    upperEdge: previousThreshold,
    lowerEdge: 'chartMin',
  });

  return regions;
};

const progressAtX = (x: number) => (x / PREVIEW_WIDTH) * 100;

const yFromProgress = (progressPercent: number) =>
  PREVIEW_HEIGHT * (1 - progressPercent / 100);

const resolvePreviewEdgeY = (x: number, edge: PreviewEdge) => {
  if (edge === 'chartMax') {
    return 0;
  }
  if (edge === 'chartMin') {
    return PREVIEW_HEIGHT;
  }

  return yFromProgress(progressAtX(x) - edge);
};

const buildPreviewPolygonPoints = (region: PreviewBandRegion): string => {
  const leftX = 0;
  const rightX = PREVIEW_WIDTH;
  const upperLeft = resolvePreviewEdgeY(leftX, region.upperEdge);
  const upperRight = resolvePreviewEdgeY(rightX, region.upperEdge);
  const lowerLeft = resolvePreviewEdgeY(leftX, region.lowerEdge);
  const lowerRight = resolvePreviewEdgeY(rightX, region.lowerEdge);

  return [
    `${leftX},${upperLeft}`,
    `${rightX},${upperRight}`,
    `${rightX},${lowerRight}`,
    `${leftX},${lowerLeft}`,
  ].join(' ');
};

export function buildPreviewBandRegions(
  bands: RagBand[],
  bandColors: Record<BandKey, string>,
): PreviewBandPolygon[] {
  return derivePreviewBandRegions(bands).map((region) => ({
    key: region.key,
    color: bandColors[region.key],
    opacity: BAND_OPACITY[region.key],
    points: buildPreviewPolygonPoints(region),
  }));
}

export function catchAllBandIndex(bands: RagBand[]): number {
  for (let i = bands.length - 1; i >= 0; i -= 1) {
    if (bands[i].enabled) {
      return i;
    }
  }
  return -1;
}

export interface BandRange {
  /** Progress % at the bottom of the band; null for the catch-all. */
  from: number | null;
  /** Progress % the band stops short of; null for the first band. */
  below: number | null;
  /** Enabled band with no usable threshold: show a placeholder, not a range. */
  blank?: boolean;
}

/** Range = 100 - threshold; disabled/blank bands never bound the band below. */
export function bandProgressRanges(bands: RagBand[]): Partial<Record<BandKey, BandRange>> {
  const ranges: Partial<Record<BandKey, BandRange>> = {};
  const enabled = bands.filter((band) => band.enabled);
  let below: number | null = null;
  enabled.forEach((band, i) => {
    const threshold = band.threshold == null ? NaN : Number(band.threshold);
    if (i === enabled.length - 1) {
      ranges[band.key] = { from: null, below };
    } else if (Number.isNaN(threshold)) {
      ranges[band.key] = { from: null, below, blank: true };
    } else {
      const from = 100 - threshold;
      ranges[band.key] = { from, below };
      below = from;
    }
  });
  return ranges;
}

export function hexToRgb(hex: string) {
  const normalized = (hex || '').replace('#', '');
  if (normalized.length !== 6) {
    return null;
  }
  return {
    r: parseInt(normalized.substring(0, 2), 16),
    g: parseInt(normalized.substring(2, 4), 16),
    b: parseInt(normalized.substring(4, 6), 16),
  };
}

export function colorDistance(a: string, b: string): number {
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  if (!rgbA || !rgbB) {
    return 999;
  }
  return Math.sqrt(
    (rgbA.r - rgbB.r) ** 2
    + (rgbA.g - rgbB.g) ** 2
    + (rgbA.b - rgbB.b) ** 2,
  );
}

export function minColorDistance(colors: Record<BandKey, string>): number {
  let minDistance = 999;
  for (let i = 0; i < BAND_KEY_ORDER.length; i += 1) {
    for (let j = i + 1; j < BAND_KEY_ORDER.length; j += 1) {
      minDistance = Math.min(
        minDistance,
        colorDistance(colors[BAND_KEY_ORDER[i]], colors[BAND_KEY_ORDER[j]]),
      );
    }
  }
  return minDistance;
}

/** The org's own band names for the label inputs: '' where a band still uses its default. */
export function customBandLabels(
  labels: Record<BandKey, string>,
  defaults: Record<BandKey, string>,
): Record<BandKey, string> {
  return BAND_KEY_ORDER.reduce((custom, key) => ({
    ...custom,
    [key]: !labels[key] || labels[key] === defaults[key] ? '' : labels[key],
  }), {} as Record<BandKey, string>);
}

/**
 * The saveRagBandColors body: only what changed, so a names-only save never rewrites the
 * colors. A name left blank or typed as the default is sent blank so the default applies.
 */
export function buildBandSavePayload(
  colors: Record<BandKey, string>,
  savedColors: Record<BandKey, string>,
  labels: Record<BandKey, string>,
  savedLabels: Record<BandKey, string>,
  defaultLabels: Record<BandKey, string>,
): { colors?: Record<BandKey, string>; labels?: Record<BandKey, string> } {
  const trimmed = customBandLabels(
    BAND_KEY_ORDER.reduce((all, key) => ({ ...all, [key]: (labels[key] ?? '').trim() }), {} as Record<BandKey, string>),
    defaultLabels,
  );
  const colorsDirty = BAND_KEY_ORDER.some((key) => colors[key] !== savedColors[key]);
  const labelsDirty = BAND_KEY_ORDER.some((key) => trimmed[key] !== savedLabels[key]);

  return {
    ...(colorsDirty ? { colors } : {}),
    ...(labelsDirty ? { labels: trimmed } : {}),
  };
}

export { PREVIEW_WIDTH, PREVIEW_HEIGHT };
